import React, { useCallback } from 'react';
import {
  Box, Text, VStack,
} from '@gluestack-ui/themed';
import { useTranslation } from 'react-i18next';

import { OralExamDriver as ExamDriver } from '@/common/api/models/oralExamDriver';
import { OralExamMediaSource } from '@/common/api/models/oralExamMediaSource';
import {
  clientRestorableOpenSources,
  isRecordingIntegrityOpen,
  isRecordingIntegrityOpenInRunState,
} from '@/common/oralExam/mediaIntegritySources';
import { OralExamMode } from '@/common/api/models/oralExamMode';
import { OralExamOperationalFault } from '@/common/api/models/oralExamOperationalFault';
import { OralExamPauseReason } from '@/common/api/models/oralExamPauseReason';
import type { OralExamRunStateResponse } from '@/common/api/models/oralExamRunStateResponse';
import { OralExamSessionPhase } from '@/common/api/models/oralExamSessionPhase';
import { OralExamQuestionText } from '@/common/components/OralExamQuestionText';
import { OralExamErrorState } from '@/common/components/oralExam/OralExamErrorState';
import { studentFaultBanner } from '@/common/oralExam/faultPresentation';
import { RECORDING_NOT_REQUIRED_INFO } from '@/common/oralExam/recordingGateCopy';
import { resolveStageGateBlock } from '@/common/oralExam/stageGatePresentation';

import { isSelfServiceRun, oralExamAskedQuestionCount, runRequiresEvaluation } from '@/common/utils/oralExam/runConduct';
import { AuthoritativeCountdown, ONE_TO_ONE_COUNTDOWN_RING_SIZE } from './AuthoritativeCountdown';
import { type OralExamDriver } from './DoneAffordance';
import { ExamWrapUp } from './ExamWrapUp';
import { OralExamPracticeBanner } from './OralExamPracticeBanner';
import { OperationalFaultStudentBanner } from './OperationalFaultStudentBanner';
import { PauseOverlay } from './PauseOverlay';
import { QuestionProgressStrip } from './QuestionProgressStrip';
import { ReconnectOverlay, useTransportReconnectOverlayVisible } from './ReconnectOverlay';
import { StudentSessionExpiredScreen } from './StudentSessionExpiredScreen';
import { SupervisorBroadcastBanner } from './SupervisorBroadcastBanner';
import { StudentRuntimeJourneyScreen } from './StudentRuntimeJourneyScreen';
import { StudentExamShell } from './StudentExamShell';
import type { StudentRuntimeJourney } from '../utils/deriveStudentRuntimeJourney';
import { shouldUseStudentExamShell } from '../utils/deriveStudentRuntimeJourney';
import { integrityBudgetPauseRemainingSeconds } from '../utils/integrityBudgetPauseRemainingSeconds';

function OperationalFaultBannerLayer({
  fault,
  selfService,
  capacity,
}: {
  fault?: OralExamOperationalFault | null;
  selfService: boolean;
  capacity?: number | null;
}) {
  if (!fault) return null;
  const banner = studentFaultBanner(fault, { selfService, capacity });
  if (!banner) return null;
  return (
    <OperationalFaultStudentBanner
      copyKey={banner.copyKey}
      defaultValue={banner.defaultValue}
      tone={banner.tone}
    />
  );
}

export type OralExamStageProps = {
  runState: OralExamRunStateResponse;
  /** Until driver lands on run state (orval), optional override for done label. */
  driver?: OralExamDriver;
  onStudentDone?: () => void;
  onOpenAnswerWindow?: () => void | Promise<void>;
  onRequestHelp?: () => void;
  isSignalingDone?: boolean;
  isRequestingHelp?: boolean;
  /** Transport disconnect / reconnecting — wired from LiveKit connection state when available. */
  isReconnecting?: boolean;
  /** Raw LiveKit connection state for integrity pause vs transport overlay. */
  liveKitConnectionState?: import('livekit-client').ConnectionState;
  /** Derived local journey from LiveKit + committed; null when not in exam room. */
  runtimeJourney?: StudentRuntimeJourney | null;
  /** Authenticated student first name for the persistent welcome heading (R3). */
  studentFirstName?: string;
  onStartExam?: () => void;
  isMarkingStudentReady?: boolean;
  startExamError?: string | null;
  studentDoneError?: string | null;
  bookingId?: string;
  supervisorName?: string;
  supervisorPhotoUrl?: string | number | { uri?: string } | null;
  supervisorFaces?: import('./SupervisorFrame').SupervisorFaceDisplay[];
  visitingLecturerId?: string | null;
  isScreenSharing?: boolean;
  /** Sources the client currently observes as down (drives instant self-heal). */
  openMediaSources?: OralExamMediaSource[];
  permissionDeniedSource?: OralExamMediaSource | null;
  onReshareScreen?: () => void;
  onEnableCamera?: () => void;
  onUnmuteMic?: () => void;
  onAbort?: () => void | Promise<void>;
  isAborting?: boolean;
  /** Called when student taps "Done" on the wrap-up screen or leaves an expired session. */
  onDone: () => void;
  /** Completed wrap-up only — runs module submit-complete UX (autosubmit path). */
  onCompletedWrapUpDone?: () => void;
};

function StudentWaitingScreen() {
  const { t } = useTranslation();

  return (
    <VStack flex={1} p="$8" justifyContent="center" alignItems="center" testID="oral-exam-student-waiting">
      <VStack space="md" alignItems="center" maxWidth={560}>
        <Text size="xl" fontWeight="semibold" color="$textHeading" textAlign="center">
          {t('oralExam.studentWaitingTitle')}
        </Text>
        <Text size="md" color="$textBody" textAlign="center">
          {t('oralExam.studentWaitingBody')}
        </Text>
      </VStack>
    </VStack>
  );
}

function RuntimeJourneyLayer({
  journey,
  isReconnecting,
  runState,
}: {
  journey: StudentRuntimeJourney;
  isReconnecting: boolean;
  runState: OralExamRunStateResponse;
}) {
  if (journey === 'pre_start_waiting') {
    return <StudentWaitingScreen />;
  }

  if (journey === 'in_exam_committed') {
    return null;
  }

  return (
    <StudentRuntimeJourneyScreen
      journey={journey}
      driver={runState.driver}
      isReconnecting={isReconnecting}
    />
  );
}

type OneToOneQuestionConsoleProps = {
  runState: OralExamRunStateResponse;
  isReconnecting?: boolean;
  openMediaSources?: OralExamMediaSource[];
  permissionDeniedSource?: OralExamMediaSource | null;
  onReshareScreen?: () => void;
  onEnableCamera?: () => void;
  onUnmuteMic?: () => void;
};

function OneToOneQuestionConsole({
  runState,
  isReconnecting = false,
  openMediaSources = [],
  permissionDeniedSource = null,
  onReshareScreen,
  onEnableCamera,
  onUnmuteMic,
}: OneToOneQuestionConsoleProps) {
  const { t } = useTranslation();
  const connectionDown = openMediaSources.includes(OralExamMediaSource.connection);
  const recordingDown = isRecordingIntegrityOpen(openMediaSources)
    || isRecordingIntegrityOpenInRunState(runState);
  const mediaDeviceDown = clientRestorableOpenSources(openMediaSources);
  const screenMediaDown = !isReconnecting
    && mediaDeviceDown.includes(OralExamMediaSource.screen);
  const showPauseOverlay = Boolean(
    connectionDown
    || recordingDown
    || (!isReconnecting && ((runState.isPaused && runState.pauseReason) || mediaDeviceDown.length > 0)),
  );
  const showTopBannerPauseOverlay = showPauseOverlay && !screenMediaDown;
  const overlayPauseReason = connectionDown
    ? OralExamPauseReason.technical
    : (runState.pauseReason ?? OralExamPauseReason.media_disabled);
  const questionText = runState.currentQuestion?.question?.trim();
  const isWaiting = !questionText;
  const showCountdown = !isWaiting && Boolean(runState.questionDeadline);
  const showTransportReconnectOverlay = useTransportReconnectOverlayVisible(isReconnecting)
    && !connectionDown;

  return (
    <Box flex={1} bg="$bgCard" testID="oral-exam-student-in-progress">
      <Box h={4} bg="$brandBase" opacity={0.35} />
      <VStack flex={1} p="$6">
        <VStack space="sm" flexShrink={0}>
          <QuestionProgressStrip
            isRecording={
              runState.recordingRequired === true
              && runState.recordingStatus === 'pending'
              && !recordingDown
            }
            isRecordingRestarting={recordingDown}
            isConnected={!isReconnecting}
            currentQuestionIndex={runState.currentQuestionIndex}
            totalQuestions={oralExamAskedQuestionCount(runState)}
            showQuestionProgress={!isWaiting}
          />
          {isWaiting ? (
            <Text size="xs" color="$textSubtle" testID="oral-exam-one-to-one-not-started">
              {t('oralExam.oneToOne.examNotStarted', { defaultValue: 'Exam not started' })}
            </Text>
          ) : null}

          {showTopBannerPauseOverlay ? (
            <PauseOverlay
              pauseReason={overlayPauseReason}
              openMediaSources={openMediaSources}
              remainingConsecutiveSeconds={integrityBudgetPauseRemainingSeconds(runState.integrityBudget)}
              permissionDeniedSource={permissionDeniedSource}
              onReshareScreen={onReshareScreen}
              onEnableCamera={onEnableCamera}
              onUnmuteMic={onUnmuteMic}
            />
          ) : null}
        </VStack>

        <VStack
          flex={1}
          minHeight={0}
          justifyContent="center"
          alignItems="center"
          space="2xl"
          position="relative"
          overflow="scroll"
          sx={{
            _web: {
              overflowY: 'auto',
              overflowX: 'hidden',
              scrollbarGutter: 'auto',
            },
          }}
        >
          {screenMediaDown ? (
            <PauseOverlay
              variant="blocking"
              pauseReason={overlayPauseReason}
              openMediaSources={openMediaSources}
              remainingConsecutiveSeconds={integrityBudgetPauseRemainingSeconds(runState.integrityBudget)}
              permissionDeniedSource={permissionDeniedSource}
              onReshareScreen={onReshareScreen}
              onEnableCamera={onEnableCamera}
              onUnmuteMic={onUnmuteMic}
            />
          ) : isWaiting ? (
            <VStack space="md" alignItems="center" maxWidth={480} testID="oral-exam-one-to-one-waiting">
              <Box w={12} h={12} borderRadius="$full" bg="$statusSuccess" opacity={0.45} />
              <Text size="xl" fontWeight="semibold" color="$textHeading" textAlign="center">
                {t('oralExam.oneToOne.waitingTitle', { defaultValue: "You're connected" })}
              </Text>
              <Text size="md" color="$textBody" textAlign="center">
                {t('oralExam.oneToOne.waitingBody', {
                  defaultValue: 'Waiting for your supervisor to start the exam. The first question will appear here.',
                })}
              </Text>
            </VStack>
          ) : (
            <VStack alignItems="center" width="100%" maxWidth={560}>
              <VStack space="sm" alignItems="center" width="100%">
                <Text
                  size="xs"
                  color="$textHint"
                  fontWeight="bold"
                  textTransform="uppercase"
                  testID="oral-exam-one-to-one-current-label"
                >
                  {t('oralExam.studentCurrentQuestionLabel', { defaultValue: 'Current question' })}
                </Text>
                <OralExamQuestionText
                  question={questionText ?? ''}
                  size="xl"
                  titleColor="$textHeading"
                  textColor="$textHeading"
                  testID="oral-exam-stage-question-text"
                />
              </VStack>
              {showCountdown ? (
                <Box mt="$12">
                  <AuthoritativeCountdown
                    key={`${runState.currentQuestion?.questionId ?? 'none'}:${runState.questionDeadline ?? 'none'}`}
                    secondsPerQuestion={runState.secondsPerQuestion}
                    questionMarkerStartedAt={runState.questionMarkerStartedAt}
                    questionDeadline={runState.questionDeadline}
                    isPaused={runState.isPaused ?? false}
                    aiExaminer={
                      runState.driver === ExamDriver.examiner_agent
                    }
                    ringSize={ONE_TO_ONE_COUNTDOWN_RING_SIZE}
                  />
                </Box>
              ) : null}
            </VStack>
          )}
        </VStack>
      </VStack>
      <ReconnectOverlay visible={showTransportReconnectOverlay} />
    </Box>
  );
}

/**
 * Student oral exam stage router: wrap-up / 1:N StudentExamShell /
 * 1:1 OneToOneQuestionConsole / journey / waiting.
 */
export function OralExamStage({
  runState,
  driver = 'lecturer',
  onStudentDone,
  onOpenAnswerWindow,
  onRequestHelp,
  isSignalingDone = false,
  isRequestingHelp = false,
  isReconnecting = false,
  liveKitConnectionState,
  isScreenSharing = false,
  runtimeJourney = null,
  studentFirstName = '',
  onStartExam,
  isMarkingStudentReady = false,
  startExamError = null,
  studentDoneError = null,
  bookingId,
  supervisorName,
  supervisorPhotoUrl,
  supervisorFaces,
  visitingLecturerId,
  openMediaSources,
  permissionDeniedSource = null,
  onReshareScreen,
  onEnableCamera,
  onUnmuteMic,
  onAbort,
  isAborting = false,
  onDone,
  onCompletedWrapUpDone,
}: OralExamStageProps) {
  const { t } = useTranslation();
  const { state } = runState;
  const resolveWrapUpDone = useCallback((
    wrapUpState: 'completed' | 'aborted' | 'closed' | 'not_started',
  ) => {
    if (
      wrapUpState === 'completed'
      && onCompletedWrapUpDone
      && runRequiresEvaluation(runState)
    ) {
      return onCompletedWrapUpDone;
    }
    return onDone;
  }, [onCompletedWrapUpDone, onDone, runState]);
  const journeyShell = (body: React.ReactNode) => (
    <VStack flex={1}>
      {isSelfServiceRun(runState) ? <OralExamPracticeBanner /> : null}
      <SupervisorBroadcastBanner broadcast={runState.supervisorBroadcast ?? null} />
      {runState.recordingRequired === false && !isSelfServiceRun(runState) ? (
        <OperationalFaultStudentBanner
          copyKey={RECORDING_NOT_REQUIRED_INFO.copyKey}
          defaultValue={RECORDING_NOT_REQUIRED_INFO.defaultValue}
          tone="info"
          testID="oral-exam-recording-not-required-banner"
        />
      ) : null}
      <OperationalFaultBannerLayer
        fault={runState.operationalFault}
        selfService={isSelfServiceRun(runState)}
        capacity={runState.capacity}
      />
      <Box flex={1} minHeight={0}>{body}</Box>
    </VStack>
  );

  if (runState.operationalFault === OralExamOperationalFault.session_expired) {
    return <StudentSessionExpiredScreen onLeave={onDone} />;
  }

  const stageGateBlock = resolveStageGateBlock(runState.stageGate);
  if (stageGateBlock) {
    return (
      <OralExamErrorState
        testID="oral-exam-error-state"
        title={t(stageGateBlock.titleKey, { defaultValue: stageGateBlock.titleDefault })}
        message={t(stageGateBlock.bodyKey, { defaultValue: stageGateBlock.bodyDefault })}
        tone="warning"
        leave={onDone}
        leaveLabel={t('oralExam.leaveExam')}
      />
    );
  }

  if (
    runState.sessionPhase === OralExamSessionPhase.post_exam
    || state === 'completed'
    || state === 'aborted'
  ) {
    const wrapUpState = state === 'completed'
      ? 'completed'
      : state === 'aborted'
        ? 'aborted'
        : 'closed';
    return (
      <ExamWrapUp
        state={wrapUpState}
        recordingRequired={runState.recordingRequired}
        requiresEvaluation={runRequiresEvaluation(runState)}
        showPresence={runState.mode !== OralExamMode.one_to_one}
        onDone={resolveWrapUpDone(wrapUpState)}
      />
    );
  }

  if (shouldUseStudentExamShell(runState, runtimeJourney)) {
    return journeyShell(
      <StudentExamShell
        runState={runState}
        studentFirstName={studentFirstName}
        driver={driver}
        onStartExam={onStartExam}
        isMarkingStudentReady={isMarkingStudentReady}
        startExamError={startExamError}
        studentDoneError={studentDoneError}
        onStudentDone={onStudentDone}
        onOpenAnswerWindow={onOpenAnswerWindow}
        onRequestHelp={onRequestHelp}
        isSignalingDone={isSignalingDone}
        isRequestingHelp={isRequestingHelp}
        isReconnecting={isReconnecting}
        liveKitConnectionState={liveKitConnectionState}
        isScreenSharing={isScreenSharing}
        bookingId={bookingId}
        supervisorName={supervisorName}
        supervisorPhotoUrl={supervisorPhotoUrl}
        supervisorFaces={supervisorFaces}
        visitingLecturerId={visitingLecturerId}
        openMediaSources={openMediaSources}
        permissionDeniedSource={permissionDeniedSource}
        onReshareScreen={onReshareScreen}
        onEnableCamera={onEnableCamera}
        onUnmuteMic={onUnmuteMic}
        onAbort={onAbort}
        isAborting={isAborting}
        onLeave={onDone}
      />,
    );
  }

  if (
    runtimeJourney
    && runtimeJourney !== 'in_exam_committed'
  ) {
    return journeyShell(
      <RuntimeJourneyLayer
        journey={runtimeJourney}
        isReconnecting={isReconnecting}
        runState={runState}
      />,
    );
  }

  if (state === 'pre_start') {
    return journeyShell(<StudentWaitingScreen />);
  }

  if (state === 'in_progress') {
    return journeyShell(
      <OneToOneQuestionConsole
        runState={runState}
        isReconnecting={isReconnecting}
        openMediaSources={openMediaSources}
        permissionDeniedSource={permissionDeniedSource}
        onReshareScreen={onReshareScreen}
        onEnableCamera={onEnableCamera}
        onUnmuteMic={onUnmuteMic}
      />,
    );
  }

  // Defensive fallback: an unrecognised run state should never render a blank
  // screen. Give the student a clear message and a way out rather than a freeze.
  return (
    <OralExamErrorState
      testID="oral-exam-student-unknown-state"
      title={t('oralExam.errorState.unknownStateTitle', {
        defaultValue: 'Waiting for the exam',
      })}
      message={t('oralExam.errorState.unknownStateBody', {
        defaultValue: 'This is taking longer than expected. You can leave and rejoin from your bookings.',
      })}
      tone="neutral"
      leave={onDone}
      leaveLabel={t('oralExam.leaveExam')}
    />
  );
}
